import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X, CircleNotch, Bed, WarningCircle, CaretUp, CaretDown } from "@phosphor-icons/react";
import { useProperty } from "@/contexts/PropertyContext";

/**
 * Postavke → Jedinice
 *
 * Jedinice su pojedinačne bookable sobe/apartmani. Sve podatke o cijeni i kapacitetu
 * drži roditeljski RoomType (vidi Postavke → Tipovi smještaja). Ovdje user
 * uređuje samo IDENTITET jedinice (naziv, bilješke).
 *
 * Layout: grupirano po RoomType-u — klik na header otvara editor tog RoomType-a.
 * Housekeeping / cleaning statusi su namjerno uklonjeni — dolaze u zaseban
 * Housekeeping tab u budućem releaseu.
 */

function L({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-foreground">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-muted-foreground">{hint}</span>}
    </label>
  );
}

function UnitForm({ initial, properties, roomTypes, defaultPropertyId, onClose, onSaved }) {
  const isNew = !initial;
  const [form, setForm] = useState(
    initial || {
      property_id: defaultPropertyId || properties[0]?.id || "",
      room_type_id: "",
      code: "",
      name: { hr: "" },
      housekeeping_status: "clean",
      notes: "",
      entry_instructions_url: "",
    }
  );
  // Per-language links; legacy single link pre-fills EN
  const [instrUrls, setInstrUrls] = useState(() => {
    const u = initial?.entry_instructions_urls || {};
    return { en: u.en || initial?.entry_instructions_url || "", hr: u.hr || "", de: u.de || "", it: u.it || "" };
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Filter room types by property
  const propRoomTypes = useMemo(
    () => roomTypes.filter((rt) => rt.property_id === form.property_id),
    [roomTypes, form.property_id]
  );

  const submit = async () => {
    setError("");
    const name = form.name?.hr?.trim();
    if (!name) { setError("Naziv jedinice je obavezan"); toast.error("Naziv jedinice je obavezan"); return; }
    if (!form.property_id) { setError("Odaberite objekt"); return; }
    if (isNew && !form.room_type_id) { setError("Odaberite tip smještaja"); return; }
    // Auto-derive `code` from the name for new units (backend requires it, but the
    // user should never see it — it's an internal identifier).
    const code = form.code?.trim() || slugify(name);
    // Pasted links without a scheme ("drive.google.com/...") get https:// automatically
    const urls = Object.fromEntries(Object.entries(instrUrls)
      .map(([k, v]) => { const t = (v || "").trim(); return [k, t && !/^https?:\/\//i.test(t) ? `https://${t}` : t]; })
      .filter(([, v]) => v));
    const instr = urls.en || "";
    setSaving(true);
    try {
      const payload = {
        property_id: form.property_id,
        room_type_id: form.room_type_id || null,
        code,
        name: form.name,
        housekeeping_status: form.housekeeping_status || "clean",
        notes: form.notes || "",
        entry_instructions_url: instr,
        entry_instructions_urls: urls,
      };
      if (isNew) {
        const { data } = await api.post("/units", payload);
        toast.success("Jedinica dodana");
        onSaved(data);
      } else {
        // On PUT we don't need property_id/room_type_id changes; keep it minimal.
        const upd = {
          code,
          name: form.name,
          housekeeping_status: form.housekeeping_status || "clean",
          notes: form.notes || "",
          entry_instructions_url: instr,
          entry_instructions_urls: urls,
        };
        const { data } = await api.put(`/units/${initial.id}`, upd);
        // Old backend silently drops unknown fields — surface that instead of "saved"
        if (Object.keys(urls).length && !data?.entry_instructions_urls) {
          const m = "Server nije spremio linkove — backend na serveru je stara verzija (napravi rebuild backenda).";
          setError(m); toast.error(m); return;
        }
        toast.success("Jedinica spremljena");
        onSaved(data);
      }
      onClose();
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      toast.error(`Spremanje nije uspjelo: ${msg}`);
    } finally { setSaving(false); }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
      <aside className="fixed right-0 top-0 z-50 flex h-[100dvh] w-full max-w-md flex-col border-l border-border bg-card shadow-2xl" data-testid="unit-drawer">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h3 className="text-base font-semibold text-foreground">{isNew ? "Nova jedinica" : `Uredi jedinicu`}</h3>
          <button onClick={onClose} className="rounded p-1.5 text-muted-foreground hover:bg-muted"><X size={18} /></button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-5">
          {error && <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>}

          {isNew && (
            <>
              <L label="Objekt *">
                <select className="rw-input" value={form.property_id} onChange={(e) => update("property_id", e.target.value)} data-testid="unit-property">
                  {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </L>
              <L label="Tip smještaja *" hint="Cijena i kapacitet se preuzimaju iz odabranog tipa.">
                <select className="rw-input" value={form.room_type_id || ""} onChange={(e) => update("room_type_id", e.target.value)} data-testid="unit-room-type">
                  <option value="">— odaberi tip —</option>
                  {propRoomTypes.map((rt) => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
                </select>
              </L>
            </>
          )}

          <L label="Naziv jedinice *" hint="npr. 'Soba 101', 'Apartman Bura', 'Studio A'">
            <input className="rw-input" value={form.name?.hr || ""} onChange={(e) => update("name", { ...(form.name || {}), hr: e.target.value })} data-testid="unit-name-hr" />
          </L>

          <L label="Linkovi na upute za ulazak" hint="Po jedan link za svaki jezik. Varijable u porukama: {unit_instructions_link_en}, {unit_instructions_link_hr}, {unit_instructions_link_de}, {unit_instructions_link_it} (ako jezik nema link, koristi se EN).">
            <div className="space-y-2">
              {["en", "hr", "de", "it"].map((lg) => (
                <div key={lg} className="flex items-center gap-2">
                  <span className="w-8 shrink-0 text-xs font-bold uppercase text-muted-foreground">{lg}{lg === "en" ? "*" : ""}</span>
                  <input
                    type="text"
                    inputMode="url"
                    className="rw-input flex-1"
                    placeholder={lg === "en" ? "https://... (zadani, šalje se ako nema prijevoda)" : "https://..."}
                    value={instrUrls[lg]}
                    onChange={(e) => setInstrUrls((u) => ({ ...u, [lg]: e.target.value }))}
                    data-testid={`unit-instructions-url-${lg}`}
                  />
                  {/^https?:\/\//i.test(instrUrls[lg] || "") && (
                    <a href={instrUrls[lg]} target="_blank" rel="noreferrer" className="flex h-9 items-center rounded-md border border-border px-3 text-xs font-semibold text-primary hover:bg-muted" data-testid={`unit-instructions-open-${lg}`}>Otvori</a>
                  )}
                </div>
              ))}
            </div>
          </L>

          <L label="Bilješke" hint="Interne bilješke — vidljive samo tvojoj ekipi (npr. 'Ključ #4', 'Nova terasa'). Ne prikazuju se gostima ni na OTA-ima.">
            <textarea rows={3} className="rw-input" value={form.notes || ""} onChange={(e) => update("notes", e.target.value)} data-testid="unit-notes" />
          </L>

          {!isNew && (
            <div className="rounded-md bg-muted/30 p-3 text-[11px] text-muted-foreground">
              <WarningCircle size={12} weight="duotone" className="mr-1 inline" />
              Cijena, kapacitet i mapiranje na kanale se uređuju u <strong>Postavke → Tipovi smještaja</strong>.
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
          <button onClick={onClose} className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted">Odustani</button>
          <button onClick={submit} disabled={saving} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50" data-testid="unit-save">
            {saving ? "Spremam..." : isNew ? "Kreiraj" : "Spremi"}
          </button>
        </div>
      </aside>
      <style>{`.rw-input { width:100%; border:1px solid hsl(var(--border)); background:hsl(var(--background)); padding:0.5rem 0.625rem; font-size:0.875rem; border-radius:0.375rem; color:hsl(var(--foreground)); } .rw-input:focus { outline:none; border-color:hsl(var(--primary)); box-shadow:0 0 0 3px hsl(var(--primary)/0.2); }`}</style>
    </>
  );
}

export default function UnitsPage() {
  const { activePropertyId } = useProperty();
  const [units, setUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [filterProperty, setFilterProperty] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const [pr, ur, rr] = await Promise.all([
        api.get("/properties"),
        api.get("/units"),
        api.get("/room-types"),
      ]);
      setProperties(pr.data || []);
      setUnits(ur.data || []);
      setRoomTypes(rr.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    const list = (units || []).filter((u) => u.active !== false);
    // Global switcher wins; falls back to the local property filter.
    const pid = activePropertyId || filterProperty;
    if (!pid) return list;
    return list.filter((u) => u.property_id === pid);
  }, [units, filterProperty, activePropertyId]);

  const rtMap = useMemo(() => Object.fromEntries(roomTypes.map((rt) => [rt.id, rt])), [roomTypes]);
  const propMap = useMemo(() => Object.fromEntries(properties.map((p) => [p.id, p])), [properties]);

  // Group units by RoomType (fallback bucket "no-room-type" for legacy rows)
  const grouped = useMemo(() => {
    const buckets = {};
    for (const u of filtered) {
      const key = u.room_type_id || `_none_${u.property_id}`;
      if (!buckets[key]) buckets[key] = { key, roomType: rtMap[u.room_type_id] || null, property: propMap[u.property_id] || null, units: [] };
      buckets[key].units.push(u);
    }
    // Same order as the calendar: property display_order, then unit display_order.
    const ord = (u) => u.display_order ?? 0;
    for (const b of Object.values(buckets)) b.units.sort((x, y) => ord(x) - ord(y));
    return Object.values(buckets).sort((a, b) => {
      const pa = a.property?.display_order ?? 0;
      const pb = b.property?.display_order ?? 0;
      if (pa !== pb) return pa - pb;
      if (a.property?.id !== b.property?.id) return (a.property?.name || "").localeCompare(b.property?.name || "");
      return ord(a.units[0]) - ord(b.units[0]);
    });
  }, [filtered, rtMap, propMap]);

  // Persist a new order for one property (flat list of unit ids, top → bottom).
  const saveOrder = async (propertyId, groupsForProp) => {
    const ids = groupsForProp.flatMap((g) => g.units.map((u) => u.id));
    setUnits((prev) => prev.map((u) => (ids.includes(u.id) ? { ...u, display_order: ids.indexOf(u.id) + 1 } : u)));
    try {
      await api.post("/units/reorder", { property_id: propertyId, unit_ids: ids });
    } catch (e) {
      toast.error(formatApiError(e));
      refresh();
    }
  };
  // Untyped units always render after room types in the calendar, so they stay last here too.
  const propGroups = (pid) => {
    const list = grouped.filter((g) => g.property?.id === pid).map((g) => ({ ...g, units: [...g.units] }));
    return [...list.filter((g) => g.roomType), ...list.filter((g) => !g.roomType)];
  };
  const moveGroup = (g, dir) => {
    const list = propGroups(g.property?.id);
    const i = list.findIndex((x) => x.key === g.key);
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    saveOrder(g.property?.id, list);
  };
  const moveUnit = (g, u, dir) => {
    const list = propGroups(g.property?.id);
    const grp = list.find((x) => x.key === g.key);
    const i = grp.units.findIndex((x) => x.id === u.id);
    const j = i + dir;
    if (j < 0 || j >= grp.units.length) return;
    [grp.units[i], grp.units[j]] = [grp.units[j], grp.units[i]];
    saveOrder(g.property?.id, list);
  };

  const del = async (u) => {
    if (!window.confirm(`Izbrisati jedinicu "${u.name?.hr || u.code}"?`)) return;
    try {
      await api.delete(`/units/${u.id}`);
      toast.success("Jedinica obrisana");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="units-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="hidden font-heading text-2xl font-bold tracking-tight text-foreground sm:block">Jedinice</h1>
          <p className="hidden text-sm text-muted-foreground sm:mt-1 sm:block">
            Uredi identitet pojedinačnih soba/apartmana. Cijena i kapacitet dolaze iz Tipova smještaja.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!activePropertyId && properties.length > 1 && (
            <select className="rw-input2" value={filterProperty} onChange={(e) => setFilterProperty(e.target.value)} data-testid="units-property-filter">
              <option value="">Svi objekti</option>
              {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          <button onClick={() => setCreating(true)} className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90" data-testid="new-unit-btn">
            <Plus size={16} weight="bold" /> Nova jedinica
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center"><CircleNotch size={28} className="animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center">
          <Bed size={36} className="mx-auto text-muted-foreground" />
          <p className="mt-3 text-sm font-medium text-foreground">Nema jedinica</p>
          <p className="mt-1 text-xs text-muted-foreground">Kreiraj tip smještaja pa dodaj jedinice, ili klikni &quot;Nova jedinica&quot;.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map((g) => (
            <section key={g.key} className="rounded-lg border border-border bg-card" data-testid={`unit-group-${g.key}`}>
              <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/20 px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-sm" style={{ background: g.roomType?.color || "#94a3b8" }} />
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      {g.roomType?.name || <em className="text-muted-foreground">Bez tipa smještaja</em>}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {g.property?.name || "—"}
                      {g.roomType && (
                        <>
                          {" • "}
                          {g.roomType.adult_spaces ?? g.roomType.capacity_adults ?? 0} odrasl. +
                          {" "}{g.roomType.children_spaces ?? g.roomType.capacity_children ?? 0} dj.
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{g.units.length} jed.</span>
                  {g.roomType && <OrderArrows
                    onUp={() => moveGroup(g, -1)}
                    onDown={() => moveGroup(g, 1)}
                    upDisabled={propGroups(g.property?.id)[0]?.key === g.key}
                    downDisabled={propGroups(g.property?.id).slice(-1)[0]?.key === g.key}
                    label="tip smještaja"
                    testId={`rt-order-${g.key}`}
                  />}
                </div>
              </header>

              <div className="divide-y divide-border">
                {g.units.map((u) => (
                  <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 hover:bg-muted/20" data-testid={`unit-row-${u.id}`}>
                    <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setEditing(u)}>
                      <p className="flex items-center gap-2 truncate text-sm font-medium text-foreground">
                        {u.name?.hr || u.code}
                        {(u.entry_instructions_url || Object.keys(u.entry_instructions_urls || {}).length) ? (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid={`unit-instructions-badge-${u.id}`}>
                            Upute ✓ {Object.keys(u.entry_instructions_urls || {}).map((l) => l.toUpperCase()).join(" ")}
                          </span>
                        ) : (
                          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700" data-testid={`unit-instructions-missing-${u.id}`}>Nema uputa</span>
                        )}
                      </p>
                      {u.notes && <p className="mt-0.5 truncate text-xs text-muted-foreground">📝 {u.notes}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      {g.units.length > 1 && (
                        <OrderArrows
                          onUp={() => moveUnit(g, u, -1)}
                          onDown={() => moveUnit(g, u, 1)}
                          upDisabled={g.units[0].id === u.id}
                          downDisabled={g.units[g.units.length - 1].id === u.id}
                          label="jedinicu"
                          testId={`unit-order-${u.id}`}
                        />
                      )}
                      <button onClick={() => setEditing(u)} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" data-testid={`unit-edit-${u.id}`}><PencilSimple size={14} /></button>
                      <button onClick={() => del(u)} className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" data-testid={`unit-del-${u.id}`}><Trash size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <UnitForm
          initial={editing}
          properties={properties}
          roomTypes={roomTypes}
          defaultPropertyId={activePropertyId || filterProperty}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => refresh()}
        />
      )}

      <style>{`.rw-input2 { border:1px solid hsl(var(--border)); background:hsl(var(--card)); padding:0.375rem 0.625rem; font-size:0.875rem; border-radius:0.375rem; color:hsl(var(--foreground)); }`}</style>
    </div>
  );
}

function slugify(s) {
  return (s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .toUpperCase()
    .slice(0, 24) || "UNIT";
}

function OrderArrows({ onUp, onDown, upDisabled, downDisabled, label, testId }) {
  const cls = "flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-25";
  return (
    <div className="flex items-center rounded-md border border-border bg-background" data-testid={testId}>
      <button type="button" onClick={onUp} disabled={upDisabled} className={cls} title={`Pomakni ${label} gore`} aria-label={`Pomakni ${label} gore`} data-testid={`${testId}-up`}><CaretUp size={13} weight="bold" /></button>
      <span className="h-4 w-px bg-border" />
      <button type="button" onClick={onDown} disabled={downDisabled} className={cls} title={`Pomakni ${label} dolje`} aria-label={`Pomakni ${label} dolje`} data-testid={`${testId}-down`}><CaretDown size={13} weight="bold" /></button>
    </div>
  );
}
